import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Toggle } from '../../components/fs';
import {
  AccountBillingView, BillingChangeResult, BillingQuote, getAccountBilling, putAccountBilling, quoteAccountBilling,
} from '../../api/platform';
import type { Plan } from '../../shared/types';
import {
  BillingTerms, PriceTerms, TermsContext, TermsPlan, TimelineEntry, addMonthsDay, bodyOf, listCentsOf, longDay, previewOf,
  refusalsOf, sameAsNow, shortDay, summaryOf, writtenHereOnly,
} from './billingTerms';
import { isoPlusDays, localDay, money, moneyExact, todayIso } from './planDisplay';
import { AccountBillingAccount } from '../../api/platform';

// ---------------------------------------------------------------------------
// The Change plan dialog (PR 2, docs/PLAYBOOK-account-overview.md; the
// handoff's screens 07, 08 and 16): plan, billing, price, a free period first
// and when — one change, previewed as it will land, applied in one request.
//
// The draft is what the form holds; the terms are what it means. Every render
// turns the one into the other and asks the twin of the server's rules
// (`billingTerms.ts`) what it would refuse and what it would charge — so a
// warning here is the server's own sentence, and Apply is off for exactly
// the reasons Apply would fail. Nothing here does arithmetic of its own.
// ---------------------------------------------------------------------------

type Mode = PriceTerms['mode'];
type Dur = 'months' | 'until' | 'forever';
type Len = '14' | '30' | '90' | 'custom';

interface Draft {
  planId: number;
  interval: 'month' | 'year';
  mode: Mode;
  pct: string;
  dur: Dur;
  months: string;
  until: string;
  agreed: string;
  /** the agreed price's contract ref or note */
  note: string;
  /** the percent's own note — internal, never shown to the customer */
  pctNote: string;
  free: { on: boolean; planId: number; len: Len; end: string; after: 'convert' | 'free'; reason: string; notify: boolean };
  when: BillingTerms['when'];
}

const LIVE = ['active', 'trialing', 'past_due', 'unpaid'];
const utcDay = (iso: string) => new Date(iso).toISOString().slice(0, 10);

/** Dollars typed ("1,200.50") as cents; NaN when it is not a number — the rules name what is wrong. */
function centsOfText(v: string): number {
  const t = v.replace(/[$,\s]/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return NaN;
  return Math.round(Number(t) * 100);
}
const whole = (v: string): number => (/^\s*\d+\s*$/.test(v) ? Number(v) : NaN);

/** Every plan the dialog can name: on sale, the one they are on (retired or not), a free period's restore point. */
function plansOf(view: AccountBillingView): Plan[] {
  const all = [...view.plans, view.plan, ...(view.restorePlan ? [view.restorePlan] : [])];
  return all.filter((p, i) => all.findIndex((q) => q.id === p.id) === i).sort((a, b) => a.sort_order - b.sort_order);
}

/** A free period that stands — one that has not ended yet. */
const standingTrial = (view: AccountBillingView) =>
  (view.trial && new Date(view.trial.expires_at).getTime() > Date.now() ? view.trial : null);

/**
 * The terms they are on, as this dialog opens on them: the terms already
 * waiting when there are some, otherwise the account's own price — a running
 * percent read as "until its end", so sending it back untouched keeps it.
 */
function standingPrice(view: AccountBillingView): { planId: number; interval: 'month' | 'year'; price: PriceTerms } {
  const w = view.pendingTerms;
  if (w) {
    const price: PriceTerms = w.price_mode === 'agreed'
      ? { mode: 'agreed', cents: w.agreed_cents ?? 0, note: w.agreed_note }
      : w.price_mode === 'discount'
        ? {
          mode: 'discount', percent: w.discount_percent ?? 0, note: w.discount_note ?? null,
          duration: w.discount_months ? { months: w.discount_months } : w.discount_until ? { until: utcDay(w.discount_until) } : 'forever',
        }
        : { mode: 'list' };
    return { planId: w.plan_id, interval: w.billing_interval, price };
  }
  const a = view.account;
  let price: PriceTerms = { mode: 'list' };
  if (a.price_override_cents !== null) price = { mode: 'agreed', cents: a.price_override_cents, note: a.price_override_note };
  else if (a.discount_percent !== null && (!a.discount_ends_at || new Date(a.discount_ends_at).getTime() > Date.now())) {
    price = {
      mode: 'discount', percent: a.discount_percent, note: a.discount_note ?? null,
      duration: a.discount_ends_at ? { until: utcDay(a.discount_ends_at) } : 'forever',
    };
  }
  return { planId: a.plan_id, interval: a.billing_interval, price };
}

/** What the rules read — the twin of the server's `termsContextFor`. */
export function contextOf(view: AccountBillingView): TermsContext {
  const a = view.account;
  const s = standingPrice(view);
  const trial = standingTrial(view);
  const p = s.price;
  return {
    now: Date.now(),
    today: todayIso(),
    plans: plansOf(view) as TermsPlan[],
    planId: s.planId,
    interval: s.interval,
    price: {
      agreedCents: p.mode === 'agreed' ? p.cents : null,
      agreedNote: p.mode === 'agreed' ? p.note : null,
      percent: p.mode === 'discount' ? p.percent : null,
      percentMonths: p.mode === 'discount' && p.duration !== 'forever' && 'months' in p.duration ? p.duration.months : null,
      percentEndsAt: p.mode === 'discount' && p.duration !== 'forever' && 'until' in p.duration ? `${p.duration.until}T12:00:00.000Z` : null,
      percentNote: p.mode === 'discount' ? p.note : null,
    },
    paidPlanId: trial?.previous_plan_id ?? a.plan_id,
    freePeriod: trial
      ? { planId: trial.plan_id, expiresAt: trial.expires_at, after: trial.auto_convert ? 'convert' : 'free', notify: trial.notify, reason: trial.note, startsAt: trial.starts_at }
      : null,
    hasSubscription: !!a.stripe_subscription_id && LIVE.includes(a.subscription_status ?? ''),
    renewalDay: a.current_period_end ? localDay(a.current_period_end) : null,
    hasCard: view.stripe.hasPaymentMethod,
    stripe: view.stripe.connected,
    billingEmail: !!a.billing_email,
    deleting: !!a.delete_after,
    mrrCents: view.mrr.cents,
    limits: view.compLimits ?? null,
  };
}

/** The form, opened on the terms they are on. */
export function draftOf(view: AccountBillingView, ctx: TermsContext): Draft {
  const s = standingPrice(view);
  const trial = standingTrial(view);
  const plans = plansOf(view);
  const paid = plans.find((p) => p.id === ctx.paidPlanId);
  const nextUp = plans.find((p) => p.trialable && !p.retired_at && paid && p.sort_order > paid.sort_order);
  const p = s.price;
  return {
    planId: s.planId,
    interval: s.interval,
    mode: p.mode,
    pct: p.mode === 'discount' ? String(p.percent) : '10',
    dur: p.mode === 'discount' ? (p.duration === 'forever' ? 'forever' : 'months' in p.duration ? 'months' : 'until') : 'months',
    months: p.mode === 'discount' && p.duration !== 'forever' && 'months' in p.duration ? String(p.duration.months) : '3',
    until: p.mode === 'discount' && p.duration !== 'forever' && 'until' in p.duration ? p.duration.until : isoPlusDays(ctx.today, 182),
    agreed: p.mode === 'agreed' ? (p.cents / 100).toFixed(p.cents % 100 ? 2 : 0) : '',
    note: p.mode === 'agreed' ? p.note ?? '' : '',
    pctNote: p.mode === 'discount' ? p.note ?? '' : '',
    free: trial
      ? {
        on: true, planId: trial.plan_id, len: 'custom', end: utcDay(trial.expires_at),
        after: trial.auto_convert ? 'convert' : 'free', reason: trial.note ?? '', notify: trial.notify,
      }
      : {
        on: false, planId: nextUp?.id ?? s.planId, len: '30', end: isoPlusDays(ctx.today, 30),
        after: 'convert', reason: '', notify: true,
      },
    // Nothing moves before the renewal unless somebody says so; with no
    // subscription there is no renewal to wait for.
    when: ctx.hasSubscription && !trial ? 'renewal' : 'now',
  };
}

/** What the form means. Numbers typed wrong stay wrong — the rules say so in words. */
export function termsOf(d: Draft): BillingTerms {
  const price: PriceTerms = d.mode === 'discount'
    ? {
      mode: 'discount', percent: whole(d.pct),
      duration: d.dur === 'forever' ? 'forever' : d.dur === 'months' ? { months: whole(d.months) } : { until: d.until },
      note: d.pctNote.trim() || null,
    }
    : d.mode === 'agreed'
      ? { mode: 'agreed', cents: centsOfText(d.agreed), note: d.note.trim() || null }
      : { mode: 'list' };
  return {
    planId: d.planId,
    interval: d.interval,
    price,
    freePeriod: d.free.on
      ? { planId: d.free.planId, endsOn: d.free.end, after: d.free.after, reason: d.free.reason.trim() || null, notify: d.free.notify }
      : null,
    when: d.when,
  };
}

/** A percent still taking money off: set, and not past its end. */
const percentRuns = (a: AccountBillingAccount) =>
  a.discount_percent !== null && (!a.discount_ends_at || new Date(a.discount_ends_at).getTime() > Date.now());

/**
 * Whether these terms keep the price that stands on the account — the
 * engine's own test (`samePriceAsColumns`): the same figures, so the same
 * discount carries on by id. A note is not a figure.
 */
function keepsStandingPrice(p: PriceTerms, a: AccountBillingAccount): boolean {
  if (p.mode === 'list') return a.price_override_cents === null && !percentRuns(a);
  if (p.mode === 'agreed') return !percentRuns(a) && a.price_override_cents === p.cents;
  if (a.price_override_cents !== null || !percentRuns(a) || a.discount_percent !== p.percent) return false;
  const d = p.duration;
  if (d === 'forever') return !a.discount_ends_at;
  if ('until' in d) return !!a.discount_ends_at && utcDay(a.discount_ends_at) === d.until;
  return false; // "for N months" starts afresh
}

/**
 * One discount at a time (Ben, 2026-09-26): a new plan, a new billing
 * period or a new price ends the agreed price or percent they have — said
 * before Apply, as the old plan modal said it. Null when nothing ends.
 */
export function endsWithChange(t: BillingTerms, view: AccountBillingView): string | null {
  const a = view.account;
  const running = a.price_override_cents !== null ? 'agreed price' : percentRuns(a) ? `${a.discount_percent}% off` : null;
  if (!running) return null;
  const kept = t.planId === a.plan_id && t.interval === a.billing_interval && keepsStandingPrice(t.price, a);
  return kept ? null : `Their current ${running} ends with this change.`;
}

/** The refusal of a change while the customer's own cancellation runs — the server's sentence. */
const cancelledWarning = (at: string) =>
  `They cancelled their subscription; it ends on ${longDay(localDay(at))}. Nothing here can change what they pay before then.`;

const perShort = (i: 'month' | 'year') => (i === 'year' ? '/yr' : '/mo');

/** The terms waiting for a later phase (076), read the way the account page shows them. */
export interface PendingPrice {
  /** "Business annual · 15% off for 12 months" */
  summary: string;
  /** the instant they begin: the renewal, or the free period's end */
  startsAt: string;
  planId: number;
  planName: string;
  interval: 'month' | 'year';
  price: PriceTerms;
  listCents: number | null;
  /** what they pay each period once the terms begin */
  totalCents: number | null;
  currency: string;
  /** the day a percent with an end stops, counted from `startsAt`; null for forever or no percent */
  discountEnds: string | null;
}

/**
 * The terms waiting for a later phase: what the Billing tab says is coming,
 * and — while a free period converts into them — what its Plan & pricing
 * rows describe, since those are the terms the first charge will carry.
 */
export function pendingPriceOf(view: AccountBillingView): PendingPrice | null {
  const w = view.pendingTerms;
  if (!w) return null;
  const s = standingPrice(view);
  const ctx = contextOf(view);
  const terms: BillingTerms = { planId: s.planId, interval: s.interval, price: s.price, freePeriod: null, when: 'now' };
  const plan = plansOf(view).find((p) => p.id === s.planId);
  const p = s.price;
  const start = localDay(w.starts_at);
  const discountEnds = p.mode !== 'discount' || p.duration === 'forever' ? null
    : 'until' in p.duration ? p.duration.until : addMonthsDay(start, p.duration.months);
  return {
    summary: summaryOf(terms, ctx),
    startsAt: w.starts_at,
    planId: s.planId,
    planName: plan?.name ?? w.plan_name ?? '',
    interval: s.interval,
    price: p,
    listCents: plan ? listCentsOf(plan, s.interval) : null,
    totalCents: previewOf(terms, ctx)?.totalCents ?? null,
    currency: plan?.currency ?? 'usd',
    discountEnds,
  };
}

/**
 * What the toast says after Apply: the account and its new terms, as the
 * handoff writes them ("Kestrel Legal · Business monthly · 15% off for 12
 * months · free until Oct 31"), then what the server says happened — never
 * what was hoped.
 */
export function changeToast(r: BillingChangeResult, accountName: string, terms: BillingTerms): string {
  const ended = r.discountsEnded;
  const told = !terms.freePeriod || terms.freePeriod.notify;
  return [
    `${accountName} · ${r.summary}.`,
    r.chargedNowCents != null ? `Charged ${moneyExact(r.chargedNowCents)} today.` : '',
    r.via === 'stripe' && !terms.freePeriod && terms.when === 'renewal' ? 'It starts at their next renewal.' : '',
    r.via === 'local' ? 'Recorded here only — there was no live Stripe subscription to change.' : '',
    ended?.percent != null ? `Their ${ended.percent}% off has ended.` : ended?.agreedCents != null ? 'Their agreed price has ended.' : '',
    r.freePeriod === 'ended' ? 'Their free period has ended.' : '',
    r.emailed ? 'The customer has been emailed.'
      : told ? 'NO EMAIL WENT OUT — outgoing mail is not configured on this server.' : '',
    r.warning ?? '',
  ].filter(Boolean).join(' ');
}

export function ChangePlanDialog({ accountId, startWith, onClose, onApplied }: {
  accountId: number;
  /** the Billing tab's "Start free period…": open with a free period first switched on */
  startWith?: 'freePeriod';
  onClose: () => void;
  /** after the change has gone through: the page re-reads and says what happened */
  onApplied: (result: BillingChangeResult, accountName: string, terms: BillingTerms) => void;
}) {
  const [view, setView] = useState<AccountBillingView | null>(null);
  const [loadError, setLoadError] = useState('');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    getAccountBilling(accountId)
      .then((v) => {
        if (!live) return;
        const d = draftOf(v, contextOf(v));
        setView(v);
        setDraft(startWith === 'freePeriod' ? { ...d, free: { ...d.free, on: true } } : d);
      })
      .catch((e) => { if (live) setLoadError((e as Error).message); });
    return () => { live = false; };
  }, [accountId, startWith]);

  if (!view || !draft) {
    return (
      <Modal title="Change plan" sub="Plan, price and free period in one change." className="pl-cp" onClose={onClose}>
        {loadError ? <div className="ad-err" role="alert">{loadError}</div> : <div className="st-muted">Loading…</div>}
      </Modal>
    );
  }
  return (
    <DialogBody
      view={view} draft={draft} setDraft={setDraft} busy={busy} error={error}
      onClose={onClose}
      onApply={async (terms) => {
        setBusy(true); setError('');
        try {
          const r = await putAccountBilling(accountId, bodyOf(terms));
          onApplied(r, view.account.name, terms);
        } catch (e) {
          setError((e as Error).message);
          setBusy(false);
        }
      }}
    />
  );
}

function DialogBody({ view, draft, setDraft, busy, error, onClose, onApply }: {
  view: AccountBillingView;
  draft: Draft;
  setDraft: (d: Draft) => void;
  busy: boolean;
  error: string;
  onClose: () => void;
  onApply: (terms: BillingTerms) => void;
}) {
  const ctx = useMemo(() => contextOf(view), [view]);
  const plans = useMemo(() => plansOf(view), [view]);
  const terms = termsOf(draft);
  const preview = previewOf(terms, ctx);
  const refusals = [
    ...(view.stripe.cancelsAt ? [cancelledWarning(view.stripe.cancelsAt)] : []),
    ...refusalsOf(terms, ctx),
  ];
  const same = sameAsNow(terms, ctx);
  const summary = summaryOf(terms, ctx);
  const plan = plans.find((p) => p.id === draft.planId);
  const nameOf = (id: number | null) => plans.find((p) => p.id === id)?.name ?? '';
  const unit = perShort(draft.interval);
  const currency = plan?.currency ?? 'usd';
  const noPrice = !plan || !((listCentsOf(plan, draft.interval) ?? 0) > 0);
  const ends = endsWithChange(terms, view);
  const local = writtenHereOnly(terms, ctx);
  const set = (patch: Partial<Draft>) => setDraft({ ...draft, ...patch });
  const setFree = (patch: Partial<Draft['free']>) => setDraft({ ...draft, free: { ...draft.free, ...patch } });

  // Stripe's own figure for "Now, prorated" — asked for when the change is
  // one, a moment after the form settles, and only the latest answer kept.
  const [quote, setQuote] = useState<{ key: string; quote: BillingQuote | null; error?: string } | null>(null);
  const quoteKey = JSON.stringify(bodyOf(terms));
  const wantsQuote = !terms.freePeriod && terms.when === 'now' && ctx.hasSubscription && ctx.stripe && !refusals.length && !same;
  const asked = useRef('');
  useEffect(() => {
    if (!wantsQuote) return;
    asked.current = quoteKey;
    const t = setTimeout(() => {
      quoteAccountBilling(view.account.id, bodyOf(terms))
        .then((r) => { if (asked.current === quoteKey) setQuote({ key: quoteKey, quote: r.quote }); })
        .catch((e) => { if (asked.current === quoteKey) setQuote({ key: quoteKey, quote: null, error: (e as Error).message }); });
    }, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteKey, wantsQuote]);
  const shownQuote = wantsQuote && quote?.key === quoteKey ? quote : null;

  const choosePlan = (p: Plan) => {
    const paidPlan = (listCentsOf(p, draft.interval) ?? 0) > 0;
    setDraft({
      ...draft,
      planId: p.id,
      // Free has nothing to discount or replace.
      ...(paidPlan ? {} : { mode: 'list' as const }),
      // The free period follows the plan when it was the same one.
      free: draft.free.planId === draft.planId && paidPlan ? { ...draft.free, planId: p.id } : draft.free,
    });
  };

  const trialPlans = plans.filter((p) => !p.retired_at && (listCentsOf(p, 'month') ?? 0) > 0);
  const email = view.account.billing_email;
  const footnote = draft.free.on && !draft.free.notify
    ? 'Pushed to Stripe and logged under your name. The customer isn’t emailed.'
    : local
      ? `Saved here${ctx.stripe ? ' — they have no subscription, so nothing is pushed to Stripe' : ''}, ${email ? `emailed to ${email}` : 'not emailed'} and logged under your name.`
      : `Pushed to Stripe, ${email ? `emailed to ${email}` : 'not emailed'} and logged under your name.`;

  return (
    <Modal
      title={`Change plan — ${view.account.name}`}
      sub="Plan, price and free period in one change."
      className="pl-cp"
      onClose={onClose}
      footer={(
        <>
          <span className="pl-cp-summary">{summary}</span>
          <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
          <button className="fs-btn primary" disabled={busy || refusals.length > 0 || same} onClick={() => onApply(terms)}
            title={same ? 'Nothing to change — these are the terms they are on.' : undefined}>
            {busy ? 'Applying…' : 'Apply changes'}
          </button>
        </>
      )}
    >
      <div className="pl-cp-body">
        <div className="pl-cp-form">
          {/* ---- Plan ---- */}
          <div className="fs-section-label" id="cp-plan">Plan</div>
          <div className="pl-cp-plans" role="radiogroup" aria-labelledby="cp-plan">
            {plans.filter((p) => !p.retired_at || p.id === ctx.planId).map((p) => {
              const cents = listCentsOf(p, draft.interval);
              const on = p.id === draft.planId;
              return (
                <button key={p.id} type="button" role="radio" aria-checked={on} className={`pl-cp-plan${on ? ' on' : ''}`}
                  onClick={() => choosePlan(p)}>
                  <span className="pl-cp-plan-name">{p.name}</span>
                  <span className="pl-cp-plan-price">{cents ? `${money(cents, p.currency)}${perShort(draft.interval)}` : 'Free'}</span>
                  {p.id === ctx.planId && <span className="pl-cp-current">Current plan</span>}
                </button>
              );
            })}
          </div>

          {/* ---- Billing ---- */}
          <div className="fs-section-label" id="cp-billing">Billing</div>
          <div className="fs-seg pl-cp-seg" role="radiogroup" aria-labelledby="cp-billing">
            {([['month', 'Monthly'], ['year', `Annual · save ${plan?.annual_discount_pct ?? 20}%`]] as const).map(([k, l]) => (
              <button key={k} type="button" role="radio" aria-checked={draft.interval === k} className={draft.interval === k ? 'on' : ''}
                onClick={() => set({ interval: k })}>{l}</button>
            ))}
          </div>

          {/* ---- Price ---- */}
          <div className="fs-section-label" id="cp-price">Price</div>
          <div className="fs-seg pl-cp-seg" role="radiogroup" aria-labelledby="cp-price">
            {([['list', 'List price'], ['discount', '% discount'], ['agreed', 'Agreed price']] as const).map(([k, l]) => (
              <button key={k} type="button" role="radio" aria-checked={draft.mode === k} className={draft.mode === k ? 'on' : ''}
                disabled={noPrice && k !== 'list'} onClick={() => set({ mode: k })}>{l}</button>
            ))}
          </div>
          <div className="pl-cp-hint">
            {draft.mode === 'list' ? `They pay the ${plan?.name ?? ''} list price.`
              : draft.mode === 'discount' ? 'A percentage off the list price. Can’t be combined with an agreed price.'
                : 'A fixed price that replaces the list price. Can’t be combined with a % discount.'}
          </div>
          {ends && <div className="pl-cp-hint pl-cp-ends">{ends}</div>}
          {draft.mode === 'discount' && (
            <div className="pl-cp-panel">
              <div className="pl-cp-row">
                <div className="fs-field">
                  <label htmlFor="cp-pct">Percent off</label>
                  <span className="pl-cp-affix pl-cp-w104">
                    <input id="cp-pct" inputMode="numeric" value={draft.pct} onChange={(e) => set({ pct: e.target.value })} />
                    <span aria-hidden="true">%</span>
                  </span>
                </div>
                <div className="fs-field">
                  <span className="pl-cp-label" id="cp-lasts">Lasts</span>
                  <div className="fs-seg pl-cp-seg" role="radiogroup" aria-labelledby="cp-lasts">
                    {([['months', 'For N months'], ['until', 'Until a date'], ['forever', 'Forever']] as const).map(([k, l]) => (
                      <button key={k} type="button" role="radio" aria-checked={draft.dur === k} className={draft.dur === k ? 'on' : ''}
                        onClick={() => set({ dur: k })}>{l}</button>
                    ))}
                  </div>
                </div>
              </div>
              {draft.dur === 'months' && (
                <div className="fs-field">
                  <label htmlFor="cp-months">Months</label>
                  <input id="cp-months" className="pl-cp-w84" inputMode="numeric" value={draft.months} onChange={(e) => set({ months: e.target.value })} />
                  {draft.interval === 'year' && <div className="hint">Whole years on annual billing: 12, 24 or 36 — the next 1, 2 or 3 renewals.</div>}
                </div>
              )}
              {draft.dur === 'until' && (
                <div className="fs-field">
                  <label htmlFor="cp-until">Until</label>
                  <input id="cp-until" type="date" className="pl-cp-w160" value={draft.until} onChange={(e) => set({ until: e.target.value })} />
                </div>
              )}
              {/* Ours, not the design's: the old percent modal kept a note, and so does the account (D2). */}
              <div className="fs-field">
                <label htmlFor="cp-pct-note">Note</label>
                <input id="cp-pct-note" value={draft.pctNote} placeholder="Internal — never shown to the customer"
                  onChange={(e) => set({ pctNote: e.target.value })} />
              </div>
            </div>
          )}
          {draft.mode === 'agreed' && (
            <div className="pl-cp-panel">
              <div className="pl-cp-row">
                <div className="fs-field">
                  <label htmlFor="cp-agreed">Agreed price</label>
                  <span className="pl-cp-affix pl-cp-w180">
                    <span aria-hidden="true">$</span>
                    <input id="cp-agreed" inputMode="decimal" value={draft.agreed} onChange={(e) => set({ agreed: e.target.value })} />
                    <span aria-hidden="true">{draft.interval === 'year' ? '/ year' : '/ month'}</span>
                  </span>
                </div>
                <div className="fs-field pl-cp-grow">
                  <label htmlFor="cp-note">Contract ref or note</label>
                  <input id="cp-note" value={draft.note} placeholder="e.g. MSA-2026-031 · signed Sep 24" onChange={(e) => set({ note: e.target.value })} />
                </div>
              </div>
            </div>
          )}

          {/* ---- Free period first ---- */}
          <div className="pl-cp-free">
            <Toggle checked={draft.free.on} onChange={(on) => setFree({ on })} label={<b>Free period first</b>} />
            <div className="pl-cp-sub">They get a plan’s features without paying until the end date.</div>
          </div>
          {draft.free.on && (
            <div className="pl-cp-panel">
              <div className="fs-field">
                <span className="pl-cp-label" id="cp-fplan">Plan during the period</span>
                <div className="fs-seg pl-cp-seg" role="radiogroup" aria-labelledby="cp-fplan">
                  {trialPlans.map((p) => (
                    <button key={p.id} type="button" role="radio" aria-checked={draft.free.planId === p.id}
                      className={draft.free.planId === p.id ? 'on' : ''} onClick={() => setFree({ planId: p.id })}>{p.name}</button>
                  ))}
                </div>
              </div>
              <div className="fs-field">
                <span className="pl-cp-label" id="cp-len">Length</span>
                <div className="pl-cp-chips" role="group" aria-labelledby="cp-len">
                  {([['14', '14 days'], ['30', '30 days'], ['90', '90 days'], ['custom', 'Custom']] as const).map(([k, l]) => (
                    <button key={k} type="button" aria-pressed={draft.free.len === k} className={`pl-cp-chip${draft.free.len === k ? ' on' : ''}`}
                      onClick={() => setFree(k === 'custom' ? { len: k } : { len: k, end: isoPlusDays(ctx.today, Number(k)) })}>{l}</button>
                  ))}
                </div>
              </div>
              <div className="fs-field">
                <label htmlFor="cp-end">Ends on</label>
                <input id="cp-end" type="date" className="pl-cp-w160" value={draft.free.end}
                  onChange={(e) => setFree({ end: e.target.value, len: 'custom' })} />
              </div>
              <div className="fs-field">
                <span className="pl-cp-label" id="cp-after">When it ends</span>
                <div role="radiogroup" aria-labelledby="cp-after">
                  {([
                    ['convert', `Convert to paid on ${plan?.name ?? ''}`,
                      `Charges ${preview?.totalCents != null ? `${money(preview.totalCents, currency)}${unit}` : '—'} from ${draft.free.end ? longDay(draft.free.end) : '—'} at the price above.`],
                    ['free', 'Drop to Free', 'Keeps their data; anything above Free switches off.'],
                  ] as const).map(([k, t, d]) => (
                    <label key={k} className={`fs-choice${draft.free.after === k ? ' on' : ''}`}>
                      <input type="radio" name="cp-after" value={k} checked={draft.free.after === k} onChange={() => setFree({ after: k })} />
                      <span><b>{t}</b><small>{d}</small></span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="fs-field">
                <label htmlFor="cp-reason">Reason</label>
                <input id="cp-reason" value={draft.free.reason} placeholder="Internal — never shown to the customer"
                  onChange={(e) => setFree({ reason: e.target.value })} />
              </div>
              <label className="fs-check pl-cp-notify">
                <input type="checkbox" checked={draft.free.notify} onChange={(e) => setFree({ notify: e.target.checked })} />
                <span>Email {email ?? 'the customer'} about the free period and when it ends</span>
              </label>
            </div>
          )}

          {/* ---- When (no free period) ---- */}
          {!draft.free.on && (
            <>
              <div className="fs-section-label" id="cp-when">When</div>
              <div className="fs-seg pl-cp-seg" role="radiogroup" aria-labelledby="cp-when">
                {([['now', 'Now, prorated'], ['renewal', 'At the next renewal'], ['now_bill_at_renewal', 'Now, billed at renewal']] as const).map(([k, l]) => (
                  <button key={k} type="button" role="radio" aria-checked={draft.when === k} className={draft.when === k ? 'on' : ''}
                    onClick={() => set({ when: k })}>{l}</button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* ---- The preview ---- */}
        <aside className="pl-cp-preview" aria-label="What they’ll be charged">
          <div className="fs-section-label">What they’ll be charged</div>
          {preview && (
            <>
              <div className="pl-cp-lines">
                {preview.lines.map((l) => (
                  <div key={l.kind} className={`pl-cp-line${l.struck ? ' struck' : ''}`}>
                    <span>
                      {l.kind === 'list' ? `${plan?.name ?? ''} list price${draft.interval === 'year' ? ' · annual' : ''}`
                        : l.kind === 'discount' ? `${l.percent}% discount`
                          : `Agreed price${l.note ? ` · ${l.note}` : ''}`}
                    </span>
                    <span className={l.kind === 'discount' ? 'pl-cp-off' : ''}>
                      {l.kind === 'discount' ? '−' : ''}{money(l.cents, currency)}{unit}
                    </span>
                  </div>
                ))}
              </div>
              <div className="pl-cp-total">
                <span>They pay</span>
                <b>{preview.totalCents != null ? `${money(preview.totalCents, currency)}${noPrice ? '' : unit}` : '—'}</b>
              </div>
              <div className="fs-section-label">Timeline</div>
              <div className="pl-cp-timeline">
                {preview.timeline.map((e, i) => (
                  <TimelineRow key={i} e={e} name={nameOf} unit={unit} currency={currency} quote={e.kind === 'now_prorated' ? shownQuote : null} />
                ))}
              </div>
              <div className="pl-cp-mrr">
                MRR {money(preview.mrr.fromCents)} → {money(preview.mrr.toCents)}
                {preview.mrr.fromDay ? (preview.mrr.drops ? ` · drops to Free ${shortDay(preview.mrr.fromDay)}` : ` from ${shortDay(preview.mrr.fromDay)}`) : ''}
              </div>
            </>
          )}
          {refusals.map((w) => <div key={w} className="pl-cp-warn" role="alert">{w}</div>)}
          {error && <div className="ad-err" role="alert">{error}</div>}
          <div className="pl-cp-foot">{footnote}</div>
        </aside>
      </div>
    </Modal>
  );
}

const DOT: Record<TimelineEntry['kind'], string> = {
  free_starts: 'free', free_continues: 'free', first_charge: 'charge', drops_to_free: 'off',
  now_prorated: 'charge', now_unbilled: 'charge', billed_at_renewal: 'charge', at_renewal: 'charge',
  discount_ends: 'off', now_local: 'charge',
};

function TimelineRow({ e, name, unit, currency, quote }: {
  e: TimelineEntry; name: (id: number | null) => string; unit: string; currency: string;
  quote: { quote: BillingQuote | null; error?: string } | null;
}) {
  const what: Record<TimelineEntry['kind'], string> = {
    free_starts: `Free ${name(e.planId)} period starts`,
    free_continues: `Free ${name(e.planId)} period continues`,
    first_charge: `First charge · ${name(e.planId)}`,
    drops_to_free: 'Drops to Free',
    now_prorated: 'Takes effect, prorated',
    now_unbilled: 'Takes effect · nothing charged today',
    billed_at_renewal: 'Their renewal, billed at the new price',
    at_renewal: 'Takes effect at renewal',
    discount_ends: 'Discount ends · back to list',
    now_local: 'Takes effect · they have no subscription to charge',
  };
  return (
    <div className="pl-cp-when">
      <span className={`pl-cp-dot ${DOT[e.kind]}`} aria-hidden="true" />
      <span className="pl-cp-what">
        <b>{e.at === 'today' ? 'Today' : longDay(e.at)}</b>
        <span>{what[e.kind]}</span>
        {quote?.quote && <span className="pl-cp-quote">Charged today: {money(quote.quote.amountCents, currency)}</span>}
        {quote?.error && <span className="pl-cp-quote">Stripe did not quote this: {quote.error}</span>}
      </span>
      <span className="pl-cp-amt">{e.cents ? `${money(e.cents, currency)}${unit}` : '$0'}</span>
    </div>
  );
}
